/**
 * useErrorLocator —— 校验「首个错误」的查找与滚动定位（引擎的「读+定位」侧）。
 *
 * 【职责边界】只做两件事：
 *  1. 按**列的实际展示顺序**遍历，找出第一处校验错误（findFirstDoc / findFirstError）；
 *  2. 把该错误所在的单元格滚进可视区（scrollToCell，避开左右钉住列遮挡）。
 * 不含写回（见 `useControlledMutations`）、不含规则判定（见 `fieldRules`）。
 *
 * 【对外只暴露两个入口】与拆前主组件的对外面完全一致：
 *  - `validateOnly`：只校验、不滚动（输入过程等场景，避免把表格滚走）；
 *  - `validateAndScroll`：校验 + 定位（提交 / 「校验」按钮）。
 * `findFirstError` / `scrollToCell` 是它们的内部实现，不对外暴露以免扩大契约面。
 */
import React from 'react';
import { useMemoizedFn } from 'ahooks';
import { subColRules, subColEditable, validateValue } from './fieldRules';
import { rowHeightPx, subRowVisible } from './rowMetrics';
import { ERR_CELL_CLASS, ADD_ERR_CLASS } from './cellError';
import type {
  ChildBase,
  ColumnMeta,
  FieldRules,
  RowBase,
  RowId,
  SubArraySpec,
  ValidationIssue,
} from './types';

/**
 * 钉住（sticky left）列占用的总宽度。
 *
 * 「钉在左侧」的列由 @bedrock table 以 `position: sticky; left: Npx` 实现，横向滚动时
 * 固定不动，会遮挡滚到左侧的单元格。故计算横向可用区时需扣除这部分宽度。
 */
function stickyLeftWidth(rowEl: HTMLElement): number {
  let width = 0;
  for (const cell of Array.from(rowEl.children) as HTMLElement[]) {
    const cs = getComputedStyle(cell);
    if (cs.position === 'sticky' && cs.left !== 'auto') width += cell.offsetWidth;
  }
  return width;
}

/**
 * 钉住（sticky right）列占用的总宽度。
 *
 * 与 stickyLeftWidth **对称**：右侧固定列（如操作列的 `fixed:'right'`）同样以
 * `position: sticky; right: Npx` 实现，横向滚动时固定不动，会**遮盖**滚到右侧的单元格。
 *
 * ⚠️ 计算右侧可用边界时必须扣除这部分宽度：否则目标单元格虽然被滚进了容器，
 * 却仍被右侧固定列压在底下（实测：操作列 154px 会盖住考核指标列的右半部分）。
 */
function stickyRightWidth(rowEl: HTMLElement): number {
  let width = 0;
  for (const cell of Array.from(rowEl.children) as HTMLElement[]) {
    const cs = getComputedStyle(cell);
    if (cs.position === 'sticky' && cs.right !== 'auto') width += cell.offsetWidth;
  }
  return width;
}

/** 校验定位滚动时，目标行顶部预留的余量（px）：避免目标行贴住容器顶部边缘不易察觉 */
const ROW_SCROLL_OFFSET = 8;

/**
 * 横向滚动使目标单元格进入可视区（避开左右两侧钉住列遮挡）。
 *
 * 可用区间为 [左侧钉住列总宽, 容器宽 - 右侧钉住列总宽]，而非 [0, 容器宽]：
 *  - 目标单元格整体位于可用区间内 → 不滚动；
 *  - 被左侧钉住列压住 → 右移（scrollLeft 减小）使其露出；
 *  - 超出右侧可用边界（含被右侧钉住列压住）→ 左移使其贴可用右缘露出。
 * 目标单元格自身即钉住列时恒可见，直接跳过。
 */
function scrollCellIntoViewX(scroller: HTMLElement, scRect: DOMRect, rowEl: HTMLElement, cell: HTMLElement) {
  if (getComputedStyle(cell).position === 'sticky') return;
  const frozenWidth = stickyLeftWidth(rowEl);
  const cellLeft = cell.getBoundingClientRect().left - scRect.left;
  const cellRight = cellLeft + cell.offsetWidth;
  // 右侧可用边界须扣除右侧钉住列宽度，否则滚完仍被操作列遮盖
  const visibleRight = scroller.clientWidth - stickyRightWidth(rowEl);
  if (cellLeft < frozenWidth) {
    scroller.scrollLeft += cellLeft - frozenWidth - ROW_SCROLL_OFFSET;
  } else if (cellRight > visibleRight) {
    scroller.scrollLeft += cellRight - visibleRight + ROW_SCROLL_OFFSET;
  }
}

/**
 * 行内错误定位锚点：优先标红单元格（输入控件错误），其次错误态添加入口（空子数组无输入控件）。
 * 二者不会同时出现 —— findFirstDoc 只返回「第一个错误」，命中添加入口时该组必然没有子项。
 */
function findErrorAnchor(rowEl: HTMLElement): HTMLElement | null {
  return (rowEl.querySelector(`.${ERR_CELL_CLASS}`) || rowEl.querySelector(`.${ADD_ERR_CLASS}`)) as HTMLElement | null;
}

export interface UseErrorLocatorParams<TRow extends RowBase, TChild extends ChildBase> {
  /** 受控主行数组 */
  value: TRow[];
  /** 列声明：**校验顺序 = 本数组顺序**（保证定位到的「第一个错误」与视觉从左到右一致） */
  columns: ColumnMeta<TRow, TChild>[];
  /** 子数组声明：仅用于「行未被虚拟滚动渲染」时按累计行高估算纵向位置 */
  subArrays: SubArraySpec<TRow, TChild>[];
  rules: FieldRules;
  /** 表单实例：仅供 `FieldRuleRender`（动态规则）求值，identity 稳定 */
  ruleForm: unknown;
  /** 表格外层容器 ref：自主查找滚动容器与目标行（不依赖 @bedrock table.scrollTo） */
  tableWrapRef: React.RefObject<HTMLDivElement>;
  /** 回写全量校验态：有错置 true、无错**必须复位** false（理由见 validateAndScroll） */
  setForceValidate: (force: boolean) => void;
}

export interface UseErrorLocatorResult {
  /** 只校验、不滚动 —— @returns 是否存在错误 */
  validateOnly: () => boolean;
  /** 校验 + 滚动定位 —— @returns 是否存在错误（true = 有错且已定位） */
  validateAndScroll: () => boolean;
}

export function useErrorLocator<TRow extends RowBase, TChild extends ChildBase>({
  value,
  columns,
  subArrays,
  rules,
  ruleForm,
  tableWrapRef,
  setForceValidate,
}: UseErrorLocatorParams<TRow, TChild>): UseErrorLocatorResult {
  /** 单行内查找第一个错误（调用方按行序推进，故全局首错 = 首个有错的行） */
  const findFirstDoc = (row: TRow): ValidationIssue<TRow> | undefined => {
    // 按 columns 的实际展示顺序遍历（而非「doc 列全部 → 子数组全部」），
    // 保证定位到的「第一个错误」与用户视觉上的从左到右顺序一致。
    for (const c of columns) {
      if (c.kind === 'doc') {
        if (!(rules[c.doc.field]?.length || c.doc.required)) continue;
        const m = validateValue(rules, c.doc.field, row[c.doc.field], undefined, ruleForm);
        if (m) return { rowId: row.id, group: 'document', field: c.doc.field, message: m };
      } else if (c.kind === 'custom') {
        // custom 列取值方式由业务方掌握（如属性列的 property_values），故由其提供判据
        if (!c.custom.validate) continue;
        const m = c.custom.validate(row);
        if (m) return { rowId: row.id, group: 'document', field: c.custom.key, message: m };
      } else {
        // 行级隐藏的组（visibleRow 为 false）整组**没有受控组件**，故不参与校验：
        // 渲染层已让该组整组消失，若仍校验，required 的组会在该行报「不能为空」，
        // 用户提交被拦却定位到一个看不见的 cell。
        if (!subRowVisible(c.sub, row)) continue;
        // 整体必填子数组（如考核指标/必要权益）：无子项即判错。
        // group/field 均为组 key → validateAndScroll 定位到该 sub 列 cell，
        // 即「+添加{emptyLabel}」按钮所在位置，引导用户补子项。
        if (c.sub.required && c.sub.getItems(row).length === 0) {
          return { rowId: row.id, group: c.sub.key, field: c.sub.key, message: `${c.sub.title}不能为空` };
        }
        for (const it of c.sub.getItems(row)) {
          for (const col of c.sub.columns) {
            // 只校验受控（可编辑）字段：同一行里个别字段只读、其余可编辑时，
            // 只读的那个退出校验，可编辑的照常校验。
            if (!subColEditable(col, it)) continue;
            const m = validateValue(subColRules(col), col.field, it[col.field], it, ruleForm);
            if (m) return { rowId: row.id, group: c.sub.key, childId: it.id, field: col.field, message: m };
          }
        }
      }
    }
    return undefined;
  };

  /** 查找第一处校验错误（纯查询，无副作用）。供「只校验」与「校验+滚动」两种模式共用 */
  const findFirstError = useMemoizedFn((): ValidationIssue<TRow> | undefined => {
    for (const row of value) {
      const first = findFirstDoc(row);
      if (first) return first;
    }
    return undefined;
  });

  /**
   * 只校验、不滚动 —— 对应「仅校验当前视图内容」的场景（如输入过程中的实时校验）。
   *
   * 与 validateAndScroll 显式分开：避免输入路径意外触发滚动、把表格滚走
   * （对齐 ProTablePlusForm 的 validateFields / validateFieldsAndScroll 两种语义）。
   *
   * @returns 是否存在错误
   */
  const validateOnly = useMemoizedFn((): boolean => !!findFirstError());

  /**
   * 滚动到出错单元格 —— 只操作**表格自身**的滚动容器，不改变外层容器滚动位置。
   *
   * 【容器】实测本表格横纵滚动都挂在 `.rock-table-body` 上（overflow auto/scroll，
   * 同时存在 scrollWidth 与 scrollHeight 溢出），故纵向改 scrollTop、横向改 scrollLeft。
   *
   * 【为何不用 scrollIntoView】它会连带滚动**所有**祖先可滚动容器（实测会把外层抽屉
   * 一起滚走），而诉求是「在表格内部把出错的 cell 滚进可视区」。
   *
   * 【横向定位】不依赖列索引映射（td 无 data-column-data-index，固定列下 th/td 索引
   * 还可能错位），改为直接取该行内实际标红（.rock-form-item-has-error）的单元格——
   * 与用户看到的红框完全一致。行已被虚拟滚动渲染才可查；未渲染时纵向按累计行高估算。
   *
   * 【钉住列遮挡】「钉在左侧」的列是 sticky 固定，不随横向滚动移动，会盖住滚到左侧的
   * 单元格。故横向可用区不是 [0, clientWidth] 而是 [钉住列总宽, clientWidth]：
   * 目标单元格需整体落在钉住列右侧；目标本身即钉住列时无需横向滚动（恒可见）。
   */
  const scrollToCell = useMemoizedFn((rowId: RowId) => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const scroller = wrap.querySelector('.rock-table-body') as HTMLElement | null;
    if (!scroller) return;
    const scRect = scroller.getBoundingClientRect();
    const rowEl = scroller.querySelector(`[data-row-key="${rowId}"]`) as HTMLElement | null;

    if (rowEl) {
      // 纵向：目标行不在可视区时才滚动（避免无谓跳动）
      const rowDelta = rowEl.getBoundingClientRect().top - scRect.top;
      if (rowDelta < 0 || rowDelta + rowEl.offsetHeight > scroller.clientHeight) {
        scroller.scrollTop += rowDelta - ROW_SCROLL_OFFSET;
      }
      // 横向：定位该行内报错的单元格（避开钉住列遮挡）。
      // 锚点含错误态添加入口 —— 空子数组的错误不在输入控件上，只靠红框类会找不到、横向不滚动。
      const errCell = findErrorAnchor(rowEl)?.closest('td') as HTMLElement | null;
      if (errCell) {
        scrollCellIntoViewX(scroller, scRect, rowEl, errCell);
      }
      return;
    }

    // 目标行未被虚拟滚动渲染：按累计行高估算纵向位置（横向无法定位，等下次校验）
    const idx = value.findIndex((r) => r.id === rowId);
    if (idx < 0) return;
    let top = 0;
    for (let i = 0; i < idx; i += 1) top += rowHeightPx(value[i], subArrays);
    scroller.scrollTop = Math.max(top - ROW_SCROLL_OFFSET, 0);
  });

  /**
   * 校验并滚动定位到第一个出错单元格 —— 外部触发（提交、「校验」按钮）用于快速定位。
   *
   * 语义对齐 .ai-doc-design.md：「findFirst 首个错误 → 定位；无错返回 false」。
   *
   * @returns 是否存在错误（true = 有错且已定位）
   */
  const validateAndScroll = useMemoizedFn((): boolean => {
    const first = findFirstError();
    // 全量校验态随校验结果**双向**同步：
    // - 有错 → true：全部必填格标红，而非仅用户编辑过的格；
    // - 无错 → false：**必须复位**。否则上一次校验失败留下的标红会一直挂着，
    //   用户表现为「我并没有触发校验，格子/『+添加』按钮却是红的」。
    setForceValidate(!!first);
    if (!first) return false;
    // 红框随 setForceValidate 渲染，需等本次渲染提交后再滚动
    // （否则查不到标红单元格，横向无法定位到出错列）
    requestAnimationFrame(() => scrollToCell(first.rowId));
    return true;
  });

  return { validateOnly, validateAndScroll };
}
